const en = {
  // Navbar
  nav: {
    features: 'Features',
    about: 'About',
    faq: 'FAQ',
    support: 'Support',
    signIn: 'Sign In',
    getStarted: 'Get Started Free',
  },

  // Footer
  footer: {
    tagline: 'The professional document scanner that keeps your files organised, secure, and always at your fingertips.',
    product: 'Product',
    company: 'Company',
    legal: 'Legal',
    links: {
      features: 'Features',
      faq: 'FAQ',
      support: 'Support',
      about: 'About Us',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      impressum: 'Legal Notice',
      cookiePolicy: 'Cookie Policy',
      dataDeletion: 'Data Deletion',
    },
    copyright: '© {year} Docudi. All rights reserved.',
    complianceNote: 'Docudi is committed to GDPR / DSGVO compliance. Your data stays yours.',
  },

  // Hero
  hero: {
    badge: 'Document Scanner App',
    headline1: 'Your documents,',
    headline2: 'organised perfectly.',
    sub: 'Scan, organise, and access all your documents from anywhere. Docudi turns paper chaos into a clean, searchable digital archive — in seconds.',
    ctaPrimary: 'Download Free',
    ctaSecondary: 'See How It Works',
    socialProof: 'Trusted by users worldwide',
    badge1: 'Scanned in 2s',
    badge2: 'End-to-end encrypted',
  },

  // Trust bar
  trust: {
    encryption: 'End-to-end encrypted',
    gdpr: 'GDPR / DSGVO compliant',
    uptime: '99.9% uptime',
    zeroKnowledge: 'Zero-knowledge architecture',
    sync: 'Sync across devices',
    rating: '5-star rated',
  },

  // Problem / Solution
  problemSolution: {
    badge: 'The Problem & Solution',
    headline: 'Stop drowning in paper. Start working smarter.',
    sub: 'Paper documents slow you down. Docudi gives you back control.',
    before: 'Before Docudi',
    after: 'With Docudi',
    problems: [
      'Wasting time searching through physical folders',
      'Important documents lost or damaged',
      'No access to files when away from the office',
      'Sharing documents is slow and insecure',
    ],
    solutions: [
      'Find any document in seconds with full-text search',
      'All documents safely backed up and encrypted',
      'Access your entire archive from any device',
      'Share securely with one tap',
    ],
  },

  // Features
  features: {
    badge: 'Features',
    headline: 'Everything you need to go paperless',
    headlineAccent: 'paperless',
    sub: 'Powerful features that make document management effortless.',
    viewAll: 'View All Features',
    items: {
      scan: { title: 'Smart Scanning', desc: 'Auto edge detection, perspective correction, and image enhancement in real time.' },
      ocr: { title: 'OCR Text Recognition', desc: 'Extract and search text from any document. Find anything instantly.' },
      org: { title: 'Smart Organisation', desc: 'Auto-categorisation, folders, and smart tags keep everything tidy.' },
      search: { title: 'Full-Text Search', desc: 'Search inside documents — not just by filename.' },
      export: { title: 'PDF & Export', desc: 'Export as PDF, JPEG, or share directly to any app.' },
      sync: { title: 'Cloud Sync', desc: 'All your documents synced across all your devices, always.' },
      security: { title: 'Bank-Grade Security', desc: 'AES-256 encryption at rest and in transit.' },
      offline: { title: 'Works Offline', desc: 'Scan and access documents even without internet.' },
    },
  },

  // How It Works
  howItWorks: {
    badge: 'How It Works',
    headline: 'From paper to digital\nin four simple steps',
    sub: 'No learning curve. No complicated setup. Just scan, organise, and access — exactly when you need it.',
    steps: [
      {
        step: '01',
        title: 'Download & Install',
        description: 'Get Docudi from the App Store or Google Play. Set up your account in under 60 seconds — no complex configuration needed.',
        detail: 'Available on iOS and Android.',
      },
      {
        step: '02',
        title: 'Scan Your First Document',
        description: 'Point your camera at any document. Docudi\'s intelligent edge detection automatically frames, crops, and enhances the image instantly.',
        detail: 'Works with invoices, contracts, ID cards, receipts, books, whiteboards, and more.',
      },
      {
        step: '03',
        title: 'Organise with Smart Tags',
        description: 'Add custom labels, create folders, and use smart collections to keep everything perfectly organised.',
        detail: 'Use OCR-powered search to find any document by content — not just by name.',
      },
      {
        step: '04',
        title: 'Access Anywhere, Securely',
        description: 'Your encrypted document vault syncs across all your devices. Access from your phone or tablet — whenever you need it.',
        detail: 'Share documents or export as PDF with one tap.',
      },
    ],
  },

  // Security
  security: {
    badge: 'Privacy & Security',
    headline: 'Your documents deserve the',
    headlineAccent: 'highest protection',
    sub: 'Privacy is not an afterthought at Docudi — it\'s a fundamental design principle.',
    items: [
      {
        title: 'End-to-End Encryption',
        description: 'All documents are encrypted using AES-256 before being transmitted or stored. Your data is protected at every stage.',
      },
      {
        title: 'GDPR / DSGVO Compliant Design',
        description: 'Docudi is built with privacy-by-design principles. Your data is processed in accordance with applicable data protection regulations.',
      },
      {
        title: 'Data Subject Rights',
        description: 'Export or permanently delete all your data at any time. We honour data subject rights as required by law.',
      },
    ],
  },

  // Cross-platform
  crossPlatform: {
    badge: 'Cross-Platform',
    headline: 'Your documents, everywhere',
    sub: 'One app, all your devices. Docudi works seamlessly across iOS and Android.',
    benefits: [
      'Instant sync across all devices',
      'Consistent experience on every platform',
      'Offline access to your documents',
      'Automatic backups',
    ],
    platforms: {
      ios: 'Available on iOS',
      android: 'Available on Android',
    },
    syncLabel: 'Syncing…',
  },

  // FAQ Preview
  faqPreview: {
    badge: 'FAQ',
    headline: 'Frequently asked questions',
    sub: 'Can\'t find what you\'re looking for? Check the full FAQ or contact us.',
    viewAll: 'View All FAQs',
    contact: 'Contact Support',
  },

  // Final CTA
  finalCta: {
    badge: 'Get Started Today',
    headline: 'Ready to go paperless?',
    sub: 'Download Docudi for free and start scanning your documents today.',
    ctaPrimary: 'Download Free',
    ctaSecondary: 'Explore Features',
    trust1: 'Free to download',
    trust2: 'No credit card required',
    trust3: 'Cancel anytime',
  },

  // FAQs
  faqs: [
    {
      category: 'Product',
      question: 'What types of documents can I scan with Docudi?',
      answer: 'Docudi works with virtually any document — invoices, contracts, receipts, ID cards, business cards, handwritten notes, books, and more. Our intelligent edge detection handles everything from standard A4 sheets to irregular shapes.',
    },
    {
      category: 'Product',
      question: 'Does Docudi work offline?',
      answer: 'Yes. Your recently accessed documents are available offline on your device. New scans are queued and synced automatically when you reconnect to the internet.',
    },
    {
      category: 'Product',
      question: 'How good is the OCR (text recognition) quality?',
      answer: 'Our OCR engine supports over 50 languages and handles both printed and handwritten text. Under normal conditions, accuracy exceeds 98% for printed documents.',
    },
    {
      category: 'Privacy & Security',
      question: 'Is my data really end-to-end encrypted?',
      answer: 'Yes. Documents are encrypted on your device using AES-256 before transmission or storage. Our infrastructure is designed so that Docudi employees cannot access your document contents.',
    },
    {
      category: 'Privacy & Security',
      question: 'Where is my data stored?',
      answer: 'Your data is stored on servers in accordance with applicable data protection regulations. See our Privacy Policy for full details.',
    },
    {
      category: 'Privacy & Security',
      question: 'Can I delete all my data permanently?',
      answer: 'Absolutely. You can request complete deletion of your account and all associated data at any time. See our Data Deletion page for details.',
    },
    {
      category: 'Billing',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes. You can cancel anytime from your account settings. Your plan remains active until the end of your billing period. There are no cancellation fees.',
    },
    {
      category: 'Billing',
      question: 'Do you offer a free trial?',
      answer: 'Yes. The app is free to download and use. Premium features may require a subscription. No credit card is required to get started.',
    },
    {
      category: 'Technical',
      question: 'Which devices and platforms are supported?',
      answer: 'Docudi is available as a native app for iOS (iPhone & iPad) and Android. All versions sync in real time.',
    },
    {
      category: 'Technical',
      question: 'How many devices can I use with one account?',
      answer: 'You can use Docudi on unlimited devices with a single subscription. All devices sync automatically.',
    },
  ],

  // Features page — advanced table
  featuresTable: {
    headline: 'Complete Feature Overview',
    sub: 'Every capability of Docudi, organised by category.',
    ctaPrimary: 'Download Free',
    categories: {
      scanning: {
        title: 'Scanning',
        items: [
          'Auto edge detection & crop',
          'Perspective correction',
          'Shadow & lighting enhancement',
          'Batch scanning mode',
          'QR & barcode reading',
          'Business card scanning',
        ],
      },
      organisation: {
        title: 'Organisation',
        items: [
          'Custom folders & sub-folders',
          'Smart tags & labels',
          'Favourites & starred documents',
          'Document date extraction',
          'Duplicate detection',
          'Version history',
        ],
      },
      search: {
        title: 'Search',
        items: [
          'Full-text OCR search',
          'Search within PDFs',
          '50+ language OCR',
          'Filter by date, type, size',
          'Recent documents view',
          'Saved search filters',
        ],
      },
      export: {
        title: 'Export & Share',
        items: [
          'Multi-page PDF creation',
          'JPEG & PNG export',
          'Password-protected PDFs',
          'Direct email sharing',
          'Cloud storage integration',
          'Print directly from app',
        ],
      },
      security: {
        title: 'Security',
        items: [
          'AES-256 encryption',
          'Two-factor authentication',
          'Biometric login',
          'App lock PIN',
          'Secure sharing links',
          'Remote device wipe',
        ],
      },
      productivity: {
        title: 'Productivity',
        items: [
          'Widgets (iOS & Android)',
          'Siri & Google Assistant shortcuts',
          'Reminder & expiry dates',
          'Document templates',
          'Web clipper browser extension',
          'Calendar integration',
        ],
      },
    },
  },

  // Pages
  pages: {
    features: {
      title: 'Features',
      headline: 'Everything you need to go paperless',
      sub: 'Powerful scanning, organisation, and security features — all in one app.',
    },
    about: {
      title: 'About Docudi',
      headline: 'Built for people who value their time',
      sub: 'We\'re on a mission to make document management simple, secure, and accessible to everyone.',
    },
    contact: {
      title: 'Contact',
      headline: 'Get in touch',
      sub: 'Questions, feedback, or support requests — we\'re here to help.',
    },
    faq: {
      title: 'FAQ',
      headline: 'Frequently asked questions',
      sub: 'Find answers to the most common questions about Docudi.',
    },
    support: {
      title: 'Support',
      headline: 'How can we help?',
      sub: 'Browse support topics, check the FAQ, or contact our team.',
    },
  },

  // Cookie banner
  cookie: {
    title: '🍪 We use cookies',
    desc: 'We use essential cookies to make Docudi work, and optional cookies to improve your experience.',
    manage: 'Manage Preferences',
    reject: 'Reject Non-Essential',
    acceptAll: 'Accept All',
    prefTitle: 'Cookie Preferences',
    back: '← Back',
    save: 'Save Preferences',
    essential: { name: 'Essential Cookies', desc: 'Required for core functionality. Cannot be disabled.' },
    functional: { name: 'Functional Cookies', desc: 'Remember your settings and personalise your experience.' },
    analytics: { name: 'Analytics Cookies', desc: 'Help us understand how users use the app.' },
    marketing: { name: 'Marketing Cookies', desc: 'Used to show relevant advertising.' },
  },

  // Legal pages
  legalPages: {
    badge: 'Legal',
    lastUpdated: 'Last updated:',
    disclaimer: '⚠️ Important: This page contains placeholder content that must be reviewed and completed by a qualified legal professional before this website is published. Do not rely on this content as legal advice or as a complete and compliant legal document.',
    privacy: {
      title: 'Privacy Policy',
      metaDesc: 'Learn how Docudi collects, processes, and protects your personal data.',
    },
    terms: {
      title: 'Terms & Conditions',
      metaDesc: 'Read the terms and conditions governing the use of Docudi.',
    },
    cookies: {
      title: 'Cookie Policy',
      metaDesc: 'Learn how Docudi uses cookies and similar tracking technologies.',
    },
    dataDeletion: {
      title: 'Data Deletion',
      metaDesc: 'Learn how to delete your data from Docudi.',
    },
  },

  // 404
  notFound: {
    title: 'Page not found',
    desc: 'The page you\'re looking for doesn\'t exist or has been moved.',
    home: 'Go Home',
    help: 'Get Help',
  },
};

export default en;
